/**
 * @fileoverview Profile details page.
 * @description A read-only view of the signed-in user's identity: email, role, tenant, banking
 * scope (when the role carries one), and privileges. GET /identity/me backs the role-level/tenant/
 * scope/privileges fields the login/verify response itself doesn't carry, same as SettingsPage's
 * own account summary — this page is the fuller identity view, SettingsPage stays the actionable
 * one (change password, active sessions).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import { useAuth } from '@/shared/lib/auth/useAuth'
import { initialsFromEmail } from '@/shared/lib/auth/user-display.utils'

import type { BankingScope, CurrentPrincipal } from '@/features/auth'
import type { TFunction } from 'i18next'

/**
 * @description Formats a role's banking-data scope as a human-readable label, or the "bank-wide"
 * label when the role carries no scope restriction at all.
 * @param {TFunction} t - The translate function.
 * @param {BankingScope} [scope] - The role's data lock, if any.
 * @returns {string} The scope's display label.
 */
function formatScope(t: TFunction, scope: BankingScope | undefined): string {
  if (!scope) {
    return t('profile.scopeUnrestricted')
  }

  return scope.type === 'branch'
    ? t('profile.scopeBranch', { branchId: scope.branchId })
    : t('profile.scopeRegion', { location: scope.location })
}

/**
 * @description The account-detail rows GET /identity/me backs. Access level and tenant are
 * rendered only when present, since the identity backend doesn't serve them yet (the mock does).
 * @param {{ principal: CurrentPrincipal }} props - The signed-in user's principal.
 */
function PrincipalDetails({ principal }: { principal: CurrentPrincipal }) {
  const { t } = useTranslation()

  return (
    <>
      {principal.activeRoleLevel !== undefined && (
        <>
          <dt className="text-slate">{t('profile.accessLevelLabel')}</dt>
          <dd className="text-ink">{principal.activeRoleLevel}</dd>
        </>
      )}
      {principal.tenantId && (
        <>
          <dt className="text-slate">{t('profile.tenantLabel')}</dt>
          <dd className="text-ink">{principal.tenantId}</dd>
        </>
      )}
      <dt className="text-slate">{t('profile.scopeLabel')}</dt>
      <dd className="text-ink">{formatScope(t, principal.bankingScope)}</dd>
    </>
  )
}

/**
 * @component
 */
function ProfilePage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const currentSessionQuery = useCurrentSession()
  const privileges = currentSessionQuery.data?.privileges ?? []

  return (
    <div className="flex max-w-2xl flex-col gap-8">
      <div>
        <h2 className="text-[20px] font-semibold text-ink">{t('profile.title')}</h2>

        <div className="mt-4 flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary text-lg font-bold text-white">
            {user ? initialsFromEmail(user.email) : ''}
          </div>
          <div>
            <p className="text-[16px] font-semibold text-ink">{user?.email}</p>
            <p className="text-[14px] text-slate">{user?.activeRoleName}</p>
          </div>
        </div>

        <dl className="mt-5 grid max-w-sm grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-[14px]">
          <dt className="text-slate">{t('profile.emailLabel')}</dt>
          <dd className="text-ink">{user?.email}</dd>
          <dt className="text-slate">{t('profile.roleLabel')}</dt>
          <dd className="text-ink">{user?.activeRoleName}</dd>
          {currentSessionQuery.data && <PrincipalDetails principal={currentSessionQuery.data} />}
        </dl>
      </div>

      {privileges.length > 0 && (
        <div>
          <h3 className="text-[16px] font-semibold text-ink">{t('profile.privilegesTitle')}</h3>
          <ul className="mt-3 flex flex-wrap gap-2">
            {privileges.map((privilege) => (
              <li
                key={privilege}
                className="rounded-full bg-surface-muted px-3 py-1 text-[12.5px] text-label"
              >
                {privilege}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default ProfilePage
